import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type BillStatus = 'draft' | 'open' | 'overdue' | 'paid';

export type Bill = {
  id: number;
  businessId: number;
  vendorId: number | null;
  billNumber: string;
  date: string;
  dueDate: string;
  amount: string;
  paidAmount: string;
  balanceDue: string;
  description: string | null;
  notes: string | null;
  status: BillStatus;
  category: string | null;
  // Removing receiptUrl as it doesn't exist in the database schema
  createdAt: string;
  updatedAt: string;
};

export type CreateBillData = {
  businessId?: number;
  vendorId?: number;
  billNumber: string;
  date: string;
  dueDate: string;
  amount: string;
  paidAmount?: string;
  balanceDue?: string;
  description?: string;
  notes?: string;
  status: BillStatus;
  category?: string;
  // Removed receiptUrl field as it doesn't exist in database
};

export type UpdateBillData = Partial<CreateBillData> & { id: number };

export function useBills(businessId: number) {
  const { toast } = useToast();

  const { 
    data: bills, 
    isLoading, 
    isError, 
    error, 
    refetch 
  } = useQuery<Bill[], Error>({
    queryKey: ['/api/expenses/bills', businessId],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/expenses/bills`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch bills');
      }
      return response.json() as Promise<Bill[]>;
    },
    enabled: !!businessId,
  });

  const createBillMutation = useMutation({
    mutationFn: async (billData: CreateBillData) => {
      const { businessId: _, ...payload } = billData;
      const response = await apiRequest('POST', '/api/expenses/bills', payload);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create bill');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/expenses/bills', businessId] });
      toast({
        title: 'Success',
        description: 'Bill created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateBillMutation = useMutation({
    mutationFn: async (billData: UpdateBillData) => {
      const { businessId: _, ...payload } = billData;
      const response = await apiRequest('PATCH', `/api/expenses/bills/${billData.id}`, payload);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update bill');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/expenses/bills', businessId] });
      toast({
        title: 'Success',
        description: 'Bill updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteBillMutation = useMutation({
    mutationFn: async (billId: number) => {
      const response = await apiRequest('DELETE', `/api/expenses/bills/${billId}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete bill');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/expenses/bills', businessId] });
      toast({
        title: 'Success',
        description: 'Bill deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    bills,
    isLoading,
    isError,
    error,
    refetch,
    createBillMutation,
    updateBillMutation,
    deleteBillMutation
  };
}
